import { useEffect } from 'react';
import { CurrentUserDto } from '../../../../../types/UserDtos';
import api from '../../../../api';
import { constObservable } from '../../../../services/observables';
import { useAppContext } from '../../../hooks/useAppContext';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { useObservable } from '../../../hooks/useObservable';
import { isFeatureEnabled } from '../../../services/Features';
import { isAssistant, isHr } from '../../../services/User';

const isHrForSurveys = (user: CurrentUserDto) => isHr(user) || isAssistant(user);

export function useShowSurveysTab() {
    const { user } = useAppContext();

    // If a user could possibly have had a survey shared with them and they're not HR
    // then we load their surveys so that we can decide if any surveys are visible.
    const shouldLoadSharedSurveys = isFeatureEnabled('surveys') && !isHrForSurveys(user);

    const { value: sharedSurveys, loading } = useObservable(
        () => (shouldLoadSharedSurveys ? api.surveys.getAll.createObservable() : constObservable([])),
        [shouldLoadSharedSurveys]
    );

    const sharedSurveyCount = sharedSurveys?.length;
    const [prevSharedSurveyCount, setPrevSharedSurveyCount] = useLocalStorage('shared-survey-count', 0);

    useEffect(() => {
        if (sharedSurveyCount === undefined || sharedSurveyCount === prevSharedSurveyCount) {
            return;
        }

        setPrevSharedSurveyCount(sharedSurveyCount);
    }, [prevSharedSurveyCount, setPrevSharedSurveyCount, sharedSurveyCount]);

    if (!shouldLoadSharedSurveys) {
        return false;
    }

    const hadSharedSurveys = !!prevSharedSurveyCount;
    const hasSharedSurveys = !!sharedSurveyCount;

    return loading ? hadSharedSurveys : hasSharedSurveys;
}
